import { Link } from "react-router-dom";
import {
  ArrowRight, AlertTriangle, CheckCircle2, ShieldCheck, FileSearch, BadgePoundSterling,
} from "lucide-react";
import SEO from "@/components/SEO";
import InlineLeadCapture from "@/components/InlineLeadCapture";

/**
 * Reusable SEO-landing-page layout.
 *
 * Props:
 *   title, description, keywords, canonical, h1, eyebrow, intro, heroImg (/images/..),
 *   sections: [{ h2, body (string, supports \n\n paragraphs), bullets? }]
 *   faq: [{ q, a }]
 *   related: [{ to, label }]
 */
export default function SeoArticle({
  title, description, keywords, canonical, h1, eyebrow, intro,
  heroImg = "/images/home-hero.jpg",
  sections = [],
  faq = [],
  related = [],
  primaryCta = { to: "/quote-review", label: "Get Your Quote Checked" },
  decisionHeadline = "Before You Commit to Repairs",
  decisionBody = "Once work starts, it becomes much harder to challenge cost or scope. A specialist quote review costs nothing to start — and can save thousands.",
  decisionCta = { to: "/quote-review", label: "Get Your Quote Checked" },
}) {
  return (
    <div data-testid="seo-article-page">
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        canonical={canonical}
        image={heroImg}
        type="article"
        faq={faq}
      />

      {/* HERO */}
      <section className="hero-gradient">
        <div className="container-phs py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              {eyebrow && <span className="eyebrow">{eyebrow}</span>}
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-[#071A33]">
                {h1}
              </h1>
              {intro && <p className="mt-6 text-lg text-[#334155] max-w-2xl leading-relaxed">{intro}</p>}
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={primaryCta.to} className="btn-primary" data-testid="seo-hero-quote-btn">
                  {primaryCta.label} <ArrowRight size={16}/>
                </Link>
                <Link to="/membership" className="btn-outline" data-testid="seo-hero-join-btn">
                  Join for £99
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="img-plate">
                <img src={heroImg} alt={h1} loading="lazy" className="w-full h-[420px] object-cover"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <article className="section-pad bg-white">
        <div className="container-phs max-w-3xl">
          {sections.map((s, i) => (
            <div key={i}>
              <section className="mb-10 last:mb-0" data-testid={`seo-section-${i}`}>
                <h2 className="font-display text-2xl sm:text-3xl text-[#071A33]">{s.h2}</h2>
                {s.body && s.body.split("\n\n").map((p, idx) => (
                  <p key={idx} className="mt-4 text-[#334155] leading-relaxed">{p}</p>
                ))}
                {s.bullets && s.bullets.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-2.5 text-[#071A33]">
                        <CheckCircle2 size={18} className="text-[#064E2E] mt-0.5 shrink-0"/>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {/* Inline lead capture after the first section — peak intent moment */}
              {i === 0 && <InlineLeadCapture />}
            </div>
          ))}
        </div>
      </article>

      {/* DECISION MOMENT CTA */}
      <section className="bg-[#F7F4ED] py-14" data-testid="seo-decision-cta">
        <div className="container-phs max-w-3xl">
          <div className="card-default border-l-4 border-[#B88A2A] bg-[#FFF8EC]">
            <div className="flex gap-3 items-start">
              <AlertTriangle size={22} className="text-[#B88A2A] mt-0.5 shrink-0"/>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl text-[#071A33]">{decisionHeadline}</h2>
                <p className="mt-3 text-[#3F2E10] leading-relaxed">
                  {decisionBody}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to={decisionCta.to} className="btn-primary" data-testid="seo-decision-quote-btn">
                    {decisionCta.label} <ArrowRight size={16}/>
                  </Link>
                  <Link to="/membership" className="btn-outline" data-testid="seo-decision-join-btn">
                    Join Park Home Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="section-pad bg-white">
          <div className="container-phs max-w-3xl">
            <span className="eyebrow">Frequently Asked</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">Common questions</h2>
            <dl className="mt-8 divide-y divide-[#D7E7DF]">
              {faq.map((f, i) => (
                <div key={i} className="py-5" data-testid={`seo-faq-${i}`}>
                  <dt className="font-display text-xl text-[#071A33]">{f.q}</dt>
                  <dd className="mt-2 text-[#334155] leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* RELATED LINKS */}
      {related.length > 0 && (
        <section className="bg-[#F7F4ED] py-14" data-testid="seo-related">
          <div className="container-phs max-w-3xl">
            <h3 className="font-display text-2xl text-[#071A33]">Related Guidance</h3>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <li key={r.to}>
                  <Link to={r.to} className="card-default hover:border-[#064E2E] flex items-center justify-between gap-3 p-5" data-testid={`seo-related-${r.to.replace(/[^a-z]+/gi,"-")}`}>
                    <span className="text-[#071A33] font-medium">{r.label}</span>
                    <ArrowRight size={16} className="text-[#064E2E]"/>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      <section className="bg-white border-t border-[#D7E7DF]">
        <div className="container-phs py-10 flex flex-wrap justify-center gap-3">
          <span className="trust-pill"><ShieldCheck size={14} className="text-[#0F766E]"/> Independent guidance</span>
          <span className="trust-pill"><FileSearch size={14} className="text-[#0F766E]"/> Quote review included</span>
          <span className="trust-pill"><BadgePoundSterling size={14} className="text-[#0F766E]"/> Reduced inspection rates</span>
        </div>
      </section>
    </div>
  );
}
